export interface IDailyGameStats {
  date: string
  played: boolean
  won: boolean
  attempts: number
  guesses: string[]
}

export interface IOverallStats {
  gamesPlayed: number
  gamesWon: number
  winPercentage: number
  currentStreak: number
  maxStreak: number
  guessDistribution: { [key: number]: number }
  averageGuesses: number
  lastPlayedDate: string
}

export class StatisticsManager {
  private static readonly STATS_STORAGE_KEY = "wordle-afrikaans-stats"
  private static readonly DAILY_STATS_PREFIX = "wordle-afrikaans-daily"

  /**
   * Get current date in YYYY-MM-DD format
   */
  private static getCurrentDateString(): string {
    return new Date().toLocaleDateString('en-CA')
  }

  /**
   * Save daily game statistics
   */
  static saveDailyStats(stats: IDailyGameStats): void {
    try {
      const storageKey = `${this.DAILY_STATS_PREFIX}-${stats.date}`
      localStorage.setItem(storageKey, JSON.stringify(stats))
      
      // Update overall statistics
      this.updateOverallStats(stats)
    } catch (error) {
      console.error('Error saving daily statistics:', error)
    }
  }

  /**
   * Get daily statistics for a specific date
   */
  static getDailyStats(date?: string): IDailyGameStats | null {
    const targetDate = date || this.getCurrentDateString()
    const storageKey = `${this.DAILY_STATS_PREFIX}-${targetDate}`

    try {
      const savedStats = localStorage.getItem(storageKey)
      if (!savedStats) {
        return null
      }

      return JSON.parse(savedStats) as IDailyGameStats
    } catch (error) {
      console.error(`Error loading daily statistics for ${targetDate}:`, error)
      return null
    }
  }

  /**
   * Update overall statistics based on daily game result
   */
  private static updateOverallStats(dailyStats: IDailyGameStats): void {
    try {
      const currentStats = this.getOverallStats()
      
      // Check if this is updating an existing game or a new game
      const existingDailyStats = this.getDailyStats(dailyStats.date)
      const isNewGame = !existingDailyStats || !existingDailyStats.played
      
      // If it's not a new game, we need to subtract the old stats first
      let adjustedCurrentStats = { ...currentStats }
      if (!isNewGame && existingDailyStats) {
        adjustedCurrentStats.gamesPlayed = Math.max(0, currentStats.gamesPlayed - 1)
        adjustedCurrentStats.gamesWon = Math.max(0, currentStats.gamesWon - (existingDailyStats.won ? 1 : 0))
        
        // Remove from guess distribution if the old game was won
        if (existingDailyStats.won && existingDailyStats.attempts > 0) {
          adjustedCurrentStats.guessDistribution = { ...currentStats.guessDistribution }
          adjustedCurrentStats.guessDistribution[existingDailyStats.attempts] = 
            Math.max(0, (adjustedCurrentStats.guessDistribution[existingDailyStats.attempts] || 0) - 1)
        }
      }

      const updatedStats: IOverallStats = {
        gamesPlayed: adjustedCurrentStats.gamesPlayed + (dailyStats.played ? 1 : 0),
        gamesWon: adjustedCurrentStats.gamesWon + (dailyStats.won ? 1 : 0),
        winPercentage: 0, // Will be calculated below
        currentStreak: this.calculateCurrentStreak(dailyStats, adjustedCurrentStats),
        maxStreak: 0, // Will be calculated below
        guessDistribution: { ...adjustedCurrentStats.guessDistribution },
        averageGuesses: 0, // Will be calculated below
        lastPlayedDate: dailyStats.played ? dailyStats.date : adjustedCurrentStats.lastPlayedDate
      }

      // Update guess distribution if game was won
      if (dailyStats.won && dailyStats.attempts > 0) {
        updatedStats.guessDistribution[dailyStats.attempts] = 
          (updatedStats.guessDistribution[dailyStats.attempts] || 0) + 1
      }

      // Calculate win percentage
      updatedStats.winPercentage = updatedStats.gamesPlayed > 0 
        ? Math.round((updatedStats.gamesWon / updatedStats.gamesPlayed) * 100)
        : 0

      // Update max streak
      updatedStats.maxStreak = Math.max(adjustedCurrentStats.maxStreak, updatedStats.currentStreak)

      // Calculate average guesses (only for won games)
      const totalGuesses = Object.entries(updatedStats.guessDistribution)
        .reduce((sum, [attempts, count]) => sum + (parseInt(attempts) * count), 0)
      updatedStats.averageGuesses = updatedStats.gamesWon > 0 
        ? Math.round((totalGuesses / updatedStats.gamesWon) * 10) / 10
        : 0

      // Save updated statistics
      localStorage.setItem(this.STATS_STORAGE_KEY, JSON.stringify(updatedStats))
    } catch (error) {
      console.error('Error updating overall statistics:', error)
    }
  }

  /**
   * Calculate current streak based on daily game result
   */
  private static calculateCurrentStreak(dailyStats: IDailyGameStats, currentStats: IOverallStats): number {
    if (!dailyStats.played) {
      return currentStats.currentStreak
    }

    if (dailyStats.won) {
      return currentStats.currentStreak + 1
    } else {
      return 0 // Streak broken
    }
  }

  /**
   * Get overall statistics
   */
  static getOverallStats(): IOverallStats {
    try {
      const savedStats = localStorage.getItem(this.STATS_STORAGE_KEY)
      if (!savedStats) {
        return this.getDefaultStats()
      }

      const parsedStats = JSON.parse(savedStats) as IOverallStats
      
      // Ensure all required properties exist (for backward compatibility)
      return {
        gamesPlayed: parsedStats.gamesPlayed || 0,
        gamesWon: parsedStats.gamesWon || 0,
        winPercentage: parsedStats.winPercentage || 0,
        currentStreak: parsedStats.currentStreak || 0,
        maxStreak: parsedStats.maxStreak || 0,
        guessDistribution: parsedStats.guessDistribution || {},
        averageGuesses: parsedStats.averageGuesses || 0,
        lastPlayedDate: parsedStats.lastPlayedDate || ""
      }
    } catch (error) {
      console.error('Error loading overall statistics:', error)
      return this.getDefaultStats()
    }
  }

  /**
   * Get default statistics structure
   */
  private static getDefaultStats(): IOverallStats {
    return {
      gamesPlayed: 0,
      gamesWon: 0,
      winPercentage: 0,
      currentStreak: 0,
      maxStreak: 0,
      guessDistribution: {},
      averageGuesses: 0,
      lastPlayedDate: ""
    }
  }

  /**
   * Record a completed game
   */
  static recordGameCompletion(won: boolean, attempts: number, guesses: string[]): void {
    const currentDate = this.getCurrentDateString()
    
    const dailyStats: IDailyGameStats = {
      date: currentDate,
      played: true,
      won,
      attempts,
      guesses
    }

    this.saveDailyStats(dailyStats)
  }

  /**
   * Get statistics for the last N days
   */
  static getRecentStats(days: number = 7): IDailyGameStats[] {
    const stats: IDailyGameStats[] = []
    const currentDate = new Date()

    for (let i = 0; i < days; i++) {
      const date = new Date(currentDate)
      date.setDate(date.getDate() - i)
      const dateString = date.toLocaleDateString('en-CA')
      
      const dailyStats = this.getDailyStats(dateString)
      if (dailyStats) {
        stats.push(dailyStats)
      }
    }

    return stats.reverse() // Return in chronological order
  }

  /**
   * Check if current streak is still valid (no missed days)
   */
  static validateCurrentStreak(): number {
    const stats = this.getOverallStats()
    if (stats.currentStreak === 0) {
      return 0
    }

    // Check if there are any missed days that would break the streak
    const currentDate = new Date()
    let validStreak = 0
    
    for (let i = 0; i < stats.currentStreak + 5; i++) { // Check a few extra days
      const date = new Date(currentDate)
      date.setDate(date.getDate() - i)
      const dateString = date.toLocaleDateString('en-CA')
      
      const dailyStats = this.getDailyStats(dateString)
      if (dailyStats && dailyStats.played && dailyStats.won) {
        validStreak++
      } else if (dailyStats && dailyStats.played && !dailyStats.won) {
        // Found a loss, streak is broken
        break
      } else if (dateString < stats.lastPlayedDate) {
        // Reached a date before last played, stop checking
        break
      }
    }

    // Update stored streak if it's different
    if (validStreak !== stats.currentStreak) {
      const updatedStats = { ...stats, currentStreak: validStreak }
      localStorage.setItem(this.STATS_STORAGE_KEY, JSON.stringify(updatedStats))
    }

    return validStreak
  }

  /**
   * Reset all statistics (for testing or user request)
   */
  static resetAllStats(): void {
    try {
      // Remove overall stats
      localStorage.removeItem(this.STATS_STORAGE_KEY)
      
      // Remove all daily stats
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (key && key.startsWith(this.DAILY_STATS_PREFIX)) {
          localStorage.removeItem(key)
        }
      }
      
      // All statistics have been reset
    } catch (error) {
      console.error('Error resetting statistics:', error)
    }
  }

  /**
   * Export statistics for sharing or backup
   */
  static exportStats(): string {
    try {
      const overallStats = this.getOverallStats()
      const recentStats = this.getRecentStats(30) // Last 30 days
      
      return JSON.stringify({
        overall: overallStats,
        recent: recentStats,
        exportDate: this.getCurrentDateString()
      }, null, 2)
    } catch (error) {
      console.error('Error exporting statistics:', error)
      return ""
    }
  }
}